import React, { useCallback } from 'react';

import { Col, Row } from 'antd-v5';
import cn from 'classnames';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { MdPlayCircleFilled } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import { useUpdatePurchaseOrderMutation } from '@modules/rtk/esb-api';
import { toastService } from '@modules/toasts';
import { navigateToPurchaseOrderDetails } from '@shared';
import { Button, Card } from '@shared/components';
import { organizationGuidSel } from '@shared/duck/selectors/general.selectors';
import { formatToUIDate } from '@utils/datesLuxon';

import { POStatusText, POStatusTypes, updatePOReasons } from '../../constants';
import { hideClosedPOModal } from '../../duck';
import styles from './POCard.less';

export const POCard = ({ order, isLoading }) => {
  const dispatch = useDispatch();
  const {
    poid,
    status,
    dateCreated,
    cartClosedDate: dateClosed,
    userAdvancements,
    totalUserAdvancements,
  } = order;

  const uiStatus = POStatusText[status];
  const itemsCount = userAdvancements?.length || totalUserAdvancements || 0;
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const [updatePO] = useUpdatePurchaseOrderMutation();

  const handleViewPODetails = () => {
    dispatch(hideClosedPOModal());
    dispatch(navigateToPurchaseOrderDetails(poid));
  };

  const handleClosePO = useCallback(() => {
    const updateAwardRequirements = async () => {
      try {
        await updatePO({
          organizationGuid: selectedOrgGuid,
          purchaseOrderId: poid,
          body: {
            purchaseOrderStatus: POStatusTypes.CLOSED,
            reason: updatePOReasons.CLOSED,
          },
        }).unwrap();

        toastService.success(
          <FormattedMessage id="PurchaseOrder.PODetailsPage.success" />,
        );
      } catch (error) {
        const errorMessage = get(error, ['data', 'errorDesc']);
        if (errorMessage) {
          toastService.error(errorMessage);
        }
      }
    };

    updateAwardRequirements();
  }, [poid, selectedOrgGuid, updatePO]);

  return (
    <Card bordered noPadding loading={isLoading}>
      <div className={styles.poContainer}>
        <div>
          <Row align="middle" gutter={16}>
            <Col>
              <div className={cn(styles.statusTag, styles[status])}>
                {status === POStatusTypes.OPEN && (
                  <MdPlayCircleFilled className={styles.openIcon} />
                )}
                <Text font="body-body-3" strong>
                  <FormattedMessage
                    id="PurchaseOrder.POCard.statusLabel"
                    className={styles.statusLabel}
                    values={{ status: uiStatus }}
                  />
                </Text>
              </div>
            </Col>
            <Col>
              <div className={styles.purchaseOrderNumber}>
                <Button
                  onClick={handleViewPODetails}
                  ghost
                  noBorder
                  uppercase={false}
                  className={styles.purchaseOrderNumberBtn}
                >
                  <FormattedMessage id="PurchaseOrder.POCard.purchaseOrderNumber" />
                  {poid}
                </Button>
              </div>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col>
              <Text font="body-body-3" strong>
                <FormattedMessage
                  id="PurchaseOrder.POCard.totalItems"
                  values={{ itemsCount }}
                />
              </Text>
            </Col>
            <Col>
              <Text font="body-body-3" strong>
                <FormattedMessage
                  id="PurchaseOrder.POCard.created"
                  values={{ dateCreated: formatToUIDate(dateCreated) }}
                />
              </Text>
            </Col>
            {dateClosed && (
              <Col>
                <Text font="body-body-3" strong>
                  <FormattedMessage
                    id="PurchaseOrder.POCard.closed"
                    values={{ dateClosed: formatToUIDate(dateClosed) }}
                  />
                </Text>
              </Col>
            )}
          </Row>
        </div>
        <div>
          <Button
            onClick={handleViewPODetails}
            ghost
            shape="round"
            size="large"
            uppercase={false}
            className={styles.actionButton}
          >
            <FormattedMessage id="PurchaseOrder.POCard.viewOrder" />
          </Button>
          {status === POStatusTypes.OPEN && (
            <Button
              onClick={handleClosePO}
              type="primary"
              shape="round"
              size="large"
              uppercase={false}
              className={styles.actionButton}
            >
              <FormattedMessage id="PurchaseOrder.POCard.closeOrder" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

POCard.propTypes = {
  order: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
};
